// Global Instructions Rule Applied!
//
// Keeps a live session's global configs fresh without a reload.
//
// Global configs are cached twice — once in the backend (AppState, built at
// startup) and once per browser (the globalConfigs Redux slice, built at app
// init) — so a cfg change used to need a backend restart AND every user
// reloading. This hook closes the browser half: it polls a cheap counter and
// re-pulls the payload only when the counter moves. Serving that counter is
// also what refreshes the backend half, so one poll covers both.
//
// Cost per tick is ~300 bytes and a single-row DB read, against the 459 kB /
// ~35ms full read it avoids. Cadence comes from the server on every tick
// (cfg.app_config app/config_poll_seconds), so it is retuned from cfg with no
// deploy — every client adopts the new interval within one interval.
//
// Mounted once, in AuthGuard, so it runs for exactly the authenticated app.
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchGlobalConfigsThunk, fetchGlobalConfigsVersion } from '../controller.ts'

const DEFAULT_POLL_SECONDS = 30
// Floor the server-supplied cadence: a bad cfg value (0, negative, a typo'd
// 0.5) must not turn every session into a hot loop against the backend.
const MIN_POLL_SECONDS = 5

interface AuthUser {
  id?: string
  email?: string
}

export function useGlobalConfigsPoll(): void {
  const dispatch = useDispatch()
  const { isAuthenticated, user } = useSelector(
    (state: any) => state.auth as { isAuthenticated?: boolean; user?: AuthUser }
  )

  // The counter this session's Redux copy was built at. null until the first
  // successful read — the first read only ADOPTS the counter, it never
  // re-pulls, because the payload was just fetched at app init.
  const seenVersion = useRef<number | null>(null)
  // Guards against a slow re-pull overlapping the next tick.
  const inFlight = useRef(false)

  const userId = user?.id
  const userEmail = user?.email

  useEffect(() => {
    if (!isAuthenticated || !userId || !userEmail) return

    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const scheduleNext = (seconds: number): void => {
      if (cancelled) return
      timer = setTimeout(tick, Math.max(MIN_POLL_SECONDS, seconds) * 1000)
    }

    const tick = async (): Promise<void> => {
      if (cancelled) return

      // A hidden tab is not worth a round trip — most sessions sit in the
      // background all day. The visibilitychange listener below polls
      // immediately on return, so nothing is missed, it is only deferred.
      if (typeof document !== 'undefined' && document.hidden) {
        scheduleNext(DEFAULT_POLL_SECONDS)
        return
      }

      if (inFlight.current) {
        scheduleNext(DEFAULT_POLL_SECONDS)
        return
      }

      inFlight.current = true
      try {
        const result = await fetchGlobalConfigsVersion()
        if (cancelled) return

        // null = the read failed, or the spine is not seeded (version 0).
        // Either way there is no information here: keep the last-seen counter
        // and try again next tick rather than re-pulling on a blip.
        if (!result || !result.version) {
          scheduleNext(DEFAULT_POLL_SECONDS)
          return
        }

        if (seenVersion.current === null) {
          seenVersion.current = result.version
        } else if (result.version !== seenVersion.current) {
          seenVersion.current = result.version
          await (dispatch as (action: unknown) => Promise<unknown>)(
            fetchGlobalConfigsThunk({ id: userId, email: userEmail })
          )
        }

        scheduleNext(result.poll_seconds || DEFAULT_POLL_SECONDS)
      } catch {
        // Never let a poll failure kill the loop — the next tick retries.
        scheduleNext(DEFAULT_POLL_SECONDS)
      } finally {
        inFlight.current = false
      }
    }

    const onVisible = (): void => {
      if (typeof document !== 'undefined' && !document.hidden) {
        if (timer) clearTimeout(timer)
        void tick()
      }
    }

    document.addEventListener('visibilitychange', onVisible)
    scheduleNext(DEFAULT_POLL_SECONDS)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isAuthenticated, userId, userEmail, dispatch])
}
